import React, { useEffect } from "react"
import { NextSeo } from "next-seo"
import { useRouter } from "next/router"
import { observer } from "mobx-react"
import Link from "next/link"
import styled from "styled-components"
import useInject from "~/hooks/useInject"
import WhyUseUs from "~/components/traffic-tickets/WhyUseUs"
import ElfGoogleReviews from "~/components/_integrations/ElfGoogleReviews"
import Wisepops from "~/components/_integrations/Wisepops"
import LandingLayout from "~/components/traffic-tickets/LandingLayout"
import BackgroundSection from "~/components/traffic-tickets/BackgroundSection"
import SearchContainer from "~/components/traffic-tickets/SearchContainer"
import Footer from "~/components/Footer"
import Spacer from "~/components/Spacer"
import WinItButton from "~/components/WinItButton"
import DesktopContainer from "~/components/DesktopContainer"
import AppStoreButton from "~/public/static/images/svg/AppStoreButton.svg"
import GooglePlayButton from "~/public/static/images/svg/GooglePlayButton.svg"
import { REQUEST_STATUSES } from "~/utilities/constants"
import HeroPhone from "~/public/static/images/png/HeroPhone.png"

const SavingsImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(
      to bottom,
      rgba(0, 0, 45, 0.3) 100%,
      rgba(0, 0, 45, 0.3) 100%
    ),
    url(${HeroPhone});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 400px;
`

const SavedUsersContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const MainContent = styled.div`
  flex: 1;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  ${(props) => props.theme.media.mobile`
    flex-direction: column;
  `};
`

const MultiWeightTitle = styled.div`
  font-family: ${(props) =>
    props.bold
      ? props.theme.fonts.avenirNextDemi
      : props.theme.fonts.avenirNextRegular};
  font-size: ${(props) => (props.large ? "2rem;" : "1.6rem;")};
  text-align: center;
  width: 350px;
  color: #ffffff;
`

const ReviewContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-top: 2em;
  padding-bottom: 2em;
`

const HowItWorks = styled.div`
  font-size: 2rem;
  text-align: center;
  font-weight: bold;
`

const ReviewCarouselContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${(props) => props.theme.fonts.avenirNextBold};
  padding-top: 1em;
  padding-bottom: 1em;
  width: 100%;
`

const StoresContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${(props) => props.theme.media.mobile`
    flex-direction: column;
  `};
`

const DownloadButton = styled.img`
  width: 200px;
  border-radius: 100px;
  margin: 0 10px;
`

const mapStore = (store) => ({
  setSelectedState: store.trafficSubmission.setSelectedState,
  trafficStatesStatus: store.trafficSubmission.trafficStatesStatus,
})

const TrafficFlowLandingPage = () => {
  const { query } = useRouter()
  const { setSelectedState, trafficStatesStatus } = useInject(mapStore)

  // Traffic states must be loaded in the store before attempting to set selected state
  if (trafficStatesStatus === REQUEST_STATUSES.complete) {
    if (query.state) {
      const success = setSelectedState({ value: query.state.toUpperCase() })
      if (!success) {
        setSelectedState({ value: "NY" })
      }
    } else {
      setSelectedState({ value: "NY" })
    }
  }

  useEffect(() => {
    if (process.browser) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [])

  return (
    <>
      <NextSeo
        title="Traffic Ticket Information | WinIt"
        description="Get a free instant quote from an experienced traffic attorney for the cost to fight your traffic ticket."
        canonical="https://www.appwinit.com/traffic-tickets"
        additionalMetaTags={[
          {
            property: "description",
            content:
              "Get a free instant quote from an experienced traffic attorney for the cost to fight your traffic ticket.",
          },
        ]}
      />
      <LandingLayout>
        <MainContent>
          <BackgroundSection>
            <SearchContainer />
          </BackgroundSection>
          <DesktopContainer>
            <Spacer desktop={20} mobile={20} />
            <HowItWorks>How it Works</HowItWorks>
            <Spacer desktop={20} mobile={20} />
            <WhyUseUs />
            <Spacer desktop={48} mobile={48} />
            <Spacer desktop={45} mobile={45} />
          </DesktopContainer>
          <SavingsImage>
            <DesktopContainer>
              <SavedUsersContainer>
                <Spacer desktop={16} mobile={16} />
                <>
                  <MultiWeightTitle bold large>
                    Use It Everywhere!
                  </MultiWeightTitle>
                  <MultiWeightTitle>
                    Access our services via online browser or the mobile app.
                  </MultiWeightTitle>
                </>
                <Spacer desktop={30} mobile={30} />
                <StoresContainer>
                  <a href="https://itunes.apple.com/us/app/winit-fight-your-tickets/id929918279">
                    <DownloadButton src={AppStoreButton} alt="App Store" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.winitllc.ticketfighter">
                    <DownloadButton src={GooglePlayButton} alt="Google Play" />
                  </a>
                </StoresContainer>
              </SavedUsersContainer>
            </DesktopContainer>
          </SavingsImage>
          <DesktopContainer>
            <Spacer desktop={20} mobile={20} />
            <ReviewContainer>
              <ReviewCarouselContainer>
                <ElfGoogleReviews />
              </ReviewCarouselContainer>
            </ReviewContainer>
          </DesktopContainer>
          <Spacer mobile={20} desktop={30} />
        </MainContent>
        <Footer noDisclaimer noMedia />
      </LandingLayout>
      <Wisepops />
    </>
  )
}

export default observer(TrafficFlowLandingPage)
